/**
 * rerenderAfterAppointmentDateChange
 * ----------------------------------
 * After a clinician / Support / Org Admin successfully commits appointment
 * date changes (often several modules in one dialog session), re-finalise
 * the PDFs that embed those dates:
 *
 *   1. Module CAR(s) (CI / SSI / DD / PAD) — for each changed module whose
 *      section is already *approved* (finalised PDF exists). Sections still
 *      in the purple "AI outputs ready for review" stage are skipped: the
 *      appointment date is only baked in at render/finalise time, and those
 *      sections must be approved before any PDF is produced.
 *   2. Patient Report — exactly once via rerenderPatientReportIfStale, after
 *      all CAR work. Multiple module date changes must never cascade into
 *      multiple concurrent PR re-renders.
 *
 * Fire-and-forget from AppointmentDateDialog. Concurrency follows the same
 * wait-then-retry (max 2 runs) contract as auto-PR / TOC QbCheck.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { TriggerRenderDocTemplate } from './TriggerRenderDocTemplate';
import { rerenderPatientReportIfStale } from './autoPatientReport';

/** Short module key → CAR report_stage string expected by renderAndStoreDocument */
export const MODULE_KEY_TO_REPORT_STAGE = {
  ci: 'Clinical Interview',
  ssi: 'Semi-Structured Interview',
  dd: 'Diagnostic Decision',
  pad: 'Post-Assessment Discussion',
};

/**
 * Short module key → *ReportApproved flag under 6_manageReports.
 *
 * We deliberately gate on Approved (not Generated). Generated-only means the
 * Documents Dashboard row is still violet "Ready for review" — AI outputs
 * exist but no finalised PDF has been produced yet, so there is nothing useful
 * to re-render for an appointment-date change.
 */
export const MODULE_KEY_TO_APPROVED_FLAG = {
  ci: 'ciReportApproved',
  ssi: 'ssiReportApproved',
  dd: 'ddReportApproved',
  pad: 'padReportApproved',
};

/**
 * Normalise callers that pass either a single moduleKey or a moduleKeys array
 * (batch dialog session). Dedupes and drops unknown keys.
 *
 * @param {string|undefined} moduleKey
 * @param {string[]|undefined} moduleKeys
 * @returns {string[]}
 */
function resolveModuleKeys(moduleKey, moduleKeys) {
  const raw = Array.isArray(moduleKeys)
    ? moduleKeys
    : (moduleKey ? [moduleKey] : []);
  const seen = new Set();
  const resolved = [];
  for (const key of raw) {
    if (!MODULE_KEY_TO_REPORT_STAGE[key] || seen.has(key)) continue;
    seen.add(key);
    resolved.push(key);
  }
  return resolved;
}

/**
 * Predict which report artefacts will actually be re-finalised after appointment
 * date changes, using the same gates as rerenderAfterAppointmentDateChange /
 * isAppointmentDatesStale. The dialog uses this so confirm/success copy and the
 * Documents CTA only appear when a re-finalise is expected — not when modules
 * are still in progress or stuck in AI-outputs-awaiting-review.
 *
 * @param {Object} params
 * @param {Object} [params.manageReports]  patient dataModel['6_manageReports']
 * @param {string[]} [params.moduleKeys]   short keys being changed this session
 * @returns {{
 *   carsToRefinalise: string[],
 *   willRefinalisePatientReport: boolean,
 *   willRefinaliseAny: boolean,
 * }}
 */
export function predictAppointmentDateReportRefinalisation({
  manageReports = {},
  moduleKeys = [],
} = {}) {
  const keys = resolveModuleKeys(undefined, moduleKeys);

  // CAR: only modules whose section is already approved/finalised.
  // Generated-but-not-approved (purple "Ready for review") is intentionally
  // excluded — no finalised PDF exists to refresh yet.
  const carsToRefinalise = keys.filter(
    (key) => manageReports?.[MODULE_KEY_TO_APPROVED_FLAG[key]] === true
  );

  // Patient Report: appointment-date staleness only applies when a PR already
  // exists AND has an approval timestamp. After save we bump
  // appointmentDatesLastChangedAt, which makes that clock newer than
  // prReportApprovedAt — matching autoPatientReport.isAppointmentDatesStale.
  const prExists =
    manageReports?.prReportApproved === true ||
    manageReports?.prReportGenerated === true;
  const willRefinalisePatientReport =
    prExists && !!manageReports?.prReportApprovedAt;

  return {
    carsToRefinalise,
    willRefinalisePatientReport,
    willRefinaliseAny:
      carsToRefinalise.length > 0 || willRefinalisePatientReport,
  };
}

/**
 * @param {Object} params
 * @param {string} params.patientId
 * @param {string} [params.moduleKey]  single short key (legacy / one-module)
 * @param {string[]} [params.moduleKeys]  preferred: all modules changed in the session
 * @param {string} [params.clinicianId]  defaults to auth.currentUser.uid
 * @param {string} [params.organisation]  org name for PR required-sections logic
 * @param {Object} [params.patientDataModel]  optional; PR helper re-reads fresh
 * @returns {Promise<{ success: boolean, cars?: Object, pr?: Object, skipped?: string }>}
 */
export async function rerenderAfterAppointmentDateChange({
  patientId,
  moduleKey,
  moduleKeys,
  clinicianId,
  organisation,
  patientDataModel,
}) {
  const uid = clinicianId || auth.currentUser?.uid;
  const keys = resolveModuleKeys(moduleKey, moduleKeys);

  if (!patientId || !uid || keys.length === 0) {
    return { success: false, skipped: 'missing-params' };
  }

  let manageReports = {};
  try {
    const snap = await getDoc(doc(db, 'patients', patientId));
    if (snap.exists()) {
      manageReports = snap.data()?.dataModel?.['6_manageReports'] || {};
    }
  } catch (err) {
    console.error('rerenderAfterAppointmentDateChange: failed to read patient doc', err);
  }

  const results = { success: true, cars: {} };

  // ── Module CARs (sequential; one entry per changed module) ───────────────
  for (const key of keys) {
    const reportStage = MODULE_KEY_TO_REPORT_STAGE[key];
    const approvedFlag = MODULE_KEY_TO_APPROVED_FLAG[key];

    if (manageReports[approvedFlag] === true) {
      try {
        results.cars[key] = await TriggerRenderDocTemplate({
          reportStage,
          clinicianId: uid,
          patientId,
          deferStatusToServer: true,
          waitAndRetry: true,
        });
      } catch (err) {
        console.error(`rerenderAfterAppointmentDateChange: CAR re-render failed (${key})`, err);
        results.cars[key] = { success: false, error: err?.message || String(err) };
      }
    } else {
      // Includes locked (never generated) and ready-for-review (generated but
      // not yet approved) — neither has a finalised PDF to refresh.
      results.cars[key] = { success: true, skipped: true, reason: 'module-not-approved' };
    }
  }

  // Back-compat: single-module callers historically read `results.car`.
  if (keys.length === 1) {
    results.car = results.cars[keys[0]];
  }

  // ── Patient Report — once only, after all CAR work ───────────────────────
  // Even if three CARs were re-finalised, we must not kick off three PR jobs.
  try {
    results.pr = await rerenderPatientReportIfStale({
      patientId,
      clinicianId: uid,
      patientDataModel,
      organisation,
    });
  } catch (err) {
    console.error('rerenderAfterAppointmentDateChange: PR re-render failed', err);
    results.pr = { success: false, error: err?.message || String(err) };
  }

  return results;
}
